/**
 * ACRES Promo — entrance modal popup
 *
 * Companion to AcresPromoBanner. Shows once per cooldown window when the active
 * banner config has banner_type = 'modal_popup' or 'both'. Dismissal is recorded
 * both in localStorage (instant UX) and on the server (cross-device cooldown).
 */
import React, { useEffect, useState } from 'react';
import { acres as acresUserService } from '../lib/services/acresService';

interface BannerPayload {
  id: string;
  banner_type: 'top_banner' | 'modal_popup' | 'both';
  headline: string;
  body_markdown?: string | null;
  cta_label?: string | null;
  cta_target?: string | null;
}

interface Props {
  onNavigateToAcres: () => void;
  /** Optional: pass the already-resolved banner from AcresPromoBanner to avoid a second fetch. */
  preloaded?: BannerPayload | null;
  /** Show modal even if banner_type is 'top_banner' (used for an explicit CTA). */
  forceShow?: boolean;
}

const LS_PREFIX = 'acres_promo_modal_dismissed_';

export const AcresPromoModal: React.FC<Props> = ({ onNavigateToAcres, preloaded, forceShow = false }) => {
  const [banner, setBanner] = useState<BannerPayload | null>(preloaded ?? null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let payload: BannerPayload | null = preloaded ?? null;
      if (!payload) {
        try {
          const res = await acresUserService.getActiveBanner();
          payload = res?.data?.banner ?? null;
        } catch (err) {
          console.warn('[AcresPromoModal] fetch failed:', err);
          return;
        }
      }
      if (cancelled || !payload) return;
      if (!forceShow && payload.banner_type === 'top_banner') return;
      const dismissed = localStorage.getItem(`${LS_PREFIX}${payload.id}`) === '1';
      if (dismissed && !forceShow) return;
      setBanner(payload);
      // Stagger so it doesn't fight with the dashboard's first paint
      const t = setTimeout(() => { if (!cancelled) setOpen(true); }, 800);
      return () => clearTimeout(t);
    })();
    return () => { cancelled = true; };
  }, [preloaded, forceShow]);

  const close = async (recordDismiss: boolean) => {
    if (busy) return;
    setBusy(true);
    setOpen(false);
    if (banner && recordDismiss) {
      try {
        localStorage.setItem(`${LS_PREFIX}${banner.id}`, '1');
        await acresUserService.dismissBanner(banner.id);
      } catch (err) {
        console.warn('[AcresPromoModal] dismiss failed:', err);
      }
    }
    setBusy(false);
  };

  const handleCta = async () => {
    await close(true);
    if (banner?.cta_target && banner.cta_target !== 'acres') {
      try { window.location.assign(banner.cta_target); return; } catch { /* noop */ }
    }
    onNavigateToAcres();
  };

  if (!banner || !open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="acres-promo-modal-headline"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => close(true)}
        aria-hidden="true"
      />
      <div className="relative max-w-lg w-full bg-gradient-to-br from-gray-900 to-gray-800 border border-yellow-500/40 rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-600 px-6 py-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl" aria-hidden="true">🎁</span>
            <h2 id="acres-promo-modal-headline" className="text-xl font-bold text-white">
              {banner.headline}
            </h2>
          </div>
          <button
            onClick={() => close(true)}
            className="text-white/90 hover:text-white p-1"
            aria-label="Close ACRES promo"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        <div className="px-6 py-6 space-y-4">
          {banner.body_markdown && (
            <p className="text-gray-200 text-sm md:text-base leading-relaxed whitespace-pre-line">
              {banner.body_markdown}
            </p>
          )}
          <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-4 text-sm text-gray-300 space-y-1">
            <div>• Complete tasks across social platforms to earn ACRES points</div>
            <div>• Redeem points for fractional shares (subject to monthly cap)</div>
            <div>• Earn lucky-draw entries and unlock achievement tiers</div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button
              onClick={() => close(true)}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 rounded-lg"
            >
              Maybe later
            </button>
            <button
              onClick={handleCta}
              className="px-5 py-2 text-sm font-semibold text-gray-900 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 rounded-lg shadow"
            >
              {banner.cta_label || 'Explore ACRES'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcresPromoModal;
